import React, { useState } from 'react';

import { alertError, alertSuccess } from '@dashboard/contexts/SnackbarManager/store';
import {
  type FDomainNameVerificationFragment,
  type FDomainNameVerificationRecordsFragment,
  useDomainNameVerificationQuery,
  useUpdateDomainNameVerificationMailFromMutation,
} from '@dashboard/generated/wp/administrator';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

type InternalProps = {
  model: FDomainNameVerificationFragment & FDomainNameVerificationRecordsFragment;
  handleClose: () => void;
};
function InternalConfigureMailFromDialog({ model, handleClose }: InternalProps) {
  const [value, setValue] = useState(() => model.mailFromConfiguration?.subdomain ?? 'waypoint');
  const [update, { loading }] = useUpdateDomainNameVerificationMailFromMutation();

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (ev) => {
    ev.preventDefault();
    try {
      await update({ variables: { id: model.id, subdomain: value } });
      alertSuccess('Mail from configuration saved');
      handleClose();
    } catch (error) {
      alertError('Mail from configuration update failed');
    }
  };

  return (
    <Dialog open onClose={handleClose}>
      <form onSubmit={onSubmit}>
        <DialogTitle>Use a custom subdomain</DialogTitle>
        <DialogContent>
          <Typography paragraph color="text.secondary">
            Use a custom MAIL FROM subdomain.
          </Typography>
          <TextField
            fullWidth
            placeholder="mail"
            InputProps={{
              endAdornment: <InputAdornment position="end">.{model.domainName}</InputAdornment>,
            }}
            margin="normal"
            autoFocus
            value={value}
            onChange={(ev) => setValue(ev.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button disabled={loading} type="submit" color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

type Props = {
  id: string;
};
export default function ConfigureMailFromDialog({ id }: Props) {
  const [open, setOpen] = useState(false);
  const { data } = useDomainNameVerificationQuery({ variables: { id } });
  const model = data?.DomainNameVerification;
  const handleClose = () => {
    setOpen(false);
  };

  const renderDialog = () => {
    if (!open || model === undefined) {
      return null;
    }
    return <InternalConfigureMailFromDialog model={model} handleClose={handleClose} />;
  };

  return (
    <>
      <Button variant="outlined" disabled={model === undefined} type="button" onClick={() => setOpen(true)}>
        Update MailFrom subdomain
      </Button>
      {renderDialog()}
    </>
  );
}
